'use client'

import * as React from 'react'
import { useMemo, useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarIcon, ChevronDownIcon, ClockIcon, PlusCircleIcon, SearchIcon } from 'lucide-react'
import { z } from 'zod'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Checkbox,
  DatePresetPicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Separator,
  Skeleton,
  toast,
} from '@ui/web/components'
import { DUE_PRESETS, START_PRESETS, typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type {
  CourseParticipant,
  EditCourseDialogBaseProps,
  EditCourseDialogProps,
  EditCourseSaveData,
  EditCourseScheduleData,
} from './types'
import { useAuthClient, useSharedTRPC } from '../../context'
import { SchoolClassSelectBase } from '../school-class-select'

// ─── Schemas ─────────────────────────────────────────────────────────────────

const settingsSchema = z.object({
  title: z.string().min(1, 'Nama tugas wajib diisi'),
  // Children picked during this session. Saving never removes anyone, so the roster the dialog
  // started with is deliberately not part of the form value.
  addedChildIds: z.array(z.string()),
})

const scheduleSchema = z
  .object({
    startDate: z.date(),
    endDate: z.date().nullable(),
  })
  .refine(
    (data) => {
      if (data.endDate === null) return true
      return data.endDate > data.startDate
    },
    { message: 'Jatuh tempo harus setelah jadwal mulai', path: ['endDate'] },
  )

// ─── Schedule trigger helpers ─────────────────────────────────────────────────

const triggerClass = cn(
  'ui:flex ui:w-full ui:min-w-0 ui:items-center ui:justify-between ui:gap-1.5 ui:font-normal',
  'ui:border ui:border-outline ui:bg-neutral-white ui:text-neutral-black',
  'ui:px-2.5 ui:py-2 ui:h-12 ui:rounded-lg ui:whitespace-nowrap ui:transition-colors ui:outline-none ui:select-none',
  'ui:focus-visible:border-blue-100 ui:focus-visible:ring-3 ui:focus-visible:ring-blue-20',
  'ui:disabled:cursor-not-allowed ui:disabled:bg-grey-10 ui:disabled:opacity-50',
)

function StartTrigger(open: boolean, label: string | null) {
  return (
    <Button baseButton variant='none' className={triggerClass}>
      <span className='ui:flex ui:flex-1 ui:items-center ui:gap-1.5 ui:truncate ui:text-left ui:text-base'>
        <CalendarIcon className='ui:size-5 ui:shrink-0' />
        <span>{label ?? 'Sekarang'}</span>
      </span>
      <ChevronDownIcon
        className={cn('ui:size-4 ui:shrink-0 ui:text-blue-100 ui:transition-transform', open && 'ui:rotate-180')}
      />
    </Button>
  )
}

function DueTrigger(open: boolean, label: string | null) {
  return (
    <Button baseButton variant='none' className={triggerClass}>
      <span className='ui:flex ui:flex-1 ui:items-center ui:gap-1.5 ui:truncate ui:text-left ui:text-base'>
        <ClockIcon className='ui:size-5 ui:shrink-0' />
        <span>{label ?? 'Kapan saja'}</span>
      </span>
      <ChevronDownIcon
        className={cn('ui:size-4 ui:shrink-0 ui:text-blue-100 ui:transition-transform', open && 'ui:rotate-180')}
      />
    </Button>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function getSubtitle(child: CourseParticipant, type: 'school' | 'web'): string {
  if (type === 'school') {
    return child.premiumId ? 'Akses premium' : 'Akses terbatas'
  }
  const seat = child.premiumId ? 'Premium' : 'Terbatas'
  return child.schoolClass?.name ? `${child.schoolClass.name} • ${seat}` : seat
}

interface ParticipantRowProps {
  child: CourseParticipant
  type: 'school' | 'web'
}

function ParticipantRow({ child, type }: ParticipantRowProps) {
  const subtitle = getSubtitle(child, type)
  return (
    <div className='srd:flex srd:items-center srd:gap-2 srd:px-4 srd:py-3'>
      <Avatar profile='user' className='srd:size-8 srd:shrink-0'>
        <AvatarImage src={child.image ?? undefined} alt={child.name} />
        <AvatarFallback>{getInitials(child.name)}</AvatarFallback>
      </Avatar>
      <div className='srd:flex-1 srd:min-w-0'>
        <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:truncate')}>{child.name}</p>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:truncate')}>{subtitle}</p>
      </div>
    </div>
  )
}

interface PickerRowProps {
  child: CourseParticipant
  selected: boolean
  onToggle: (id: string) => void
  type: 'school' | 'web'
}

function PickerRow({ child, selected, onToggle, type }: PickerRowProps) {
  const subtitle = getSubtitle(child, type)
  return (
    <div
      role='button'
      tabIndex={0}
      onClick={() => onToggle(child.id)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle(child.id)}
      className='srd:flex srd:items-center srd:gap-2 srd:px-4 srd:py-3 srd:cursor-pointer srd:hover:bg-grey-10 srd:transition-colors srd:select-none'
    >
      <Avatar profile='user' className='srd:size-8 srd:shrink-0'>
        <AvatarImage src={child.image ?? undefined} alt={child.name} />
        <AvatarFallback>{getInitials(child.name)}</AvatarFallback>
      </Avatar>
      <div className='srd:flex-1 srd:min-w-0'>
        <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:truncate')}>{child.name}</p>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:truncate')}>{subtitle}</p>
      </div>
      <div role='presentation' onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={() => onToggle(child.id)} aria-label={`Pilih ${child.name}`} />
      </div>
    </div>
  )
}

// ─── Base Component ───────────────────────────────────────────────────────────

export function EditCourseDialogBase({
  type,
  open,
  onOpenChange,
  course,
  participants,
  initialParticipantIds,
  availableChildren,
  isLoadingAvailable,
  teams,
  isLoadingTeams,
  showDash,
  availableSearch,
  onAvailableSearchChange,
  availableTeamId,
  onAvailableTeamChange,
  hasStarted,
  readOnly = false,
  onSave,
  isSaving,
  onUpdateSchedule,
  isUpdatingSchedule,
  onScheduleSuccess,
}: EditCourseDialogBaseProps) {
  const childLabel = type === 'school' ? 'murid' : 'anak'

  const [step, setStep] = useState<'settings' | 'add-participants' | 'schedule'>('settings')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [participantSearch, setParticipantSearch] = useState('')
  const [addedChildren, setAddedChildren] = useState<CourseParticipant[]>([])

  // Stable initial values — useRef captures these once on mount.
  // Without this, new Date() and .map() create new references every render,
  // causing TanStack Form's useIsomorphicLayoutEffect → FormApi.update → batch()
  // → forceStoreRerender infinite loop.
  //
  // Prefer `initialParticipantIds` (the enrollment ids the caller already holds) over
  // `participants`, which is derived from an in-flight `listChildren` query and is therefore empty
  // on the first render. This only drives the participant count and the picker filter now — the
  // save payload carries additions alone, so a stale value here can no longer unenroll anyone.
  const enrolledChildIds = React.useRef(initialParticipantIds ?? participants.map((p) => p.id)).current
  const initialStartDate = React.useRef(course.startDate ?? course.createdAt ?? new Date()).current
  const initialEndDate = React.useRef(course.endDate ?? null).current

  // ── Settings form ──
  const settingsForm = useForm({
    defaultValues: {
      title: course.title,
      addedChildIds: [] as string[],
    },
    validators: { onSubmit: settingsSchema },
    onSubmit: async ({ value }) => {
      await onSave({ title: value.title, addChildIds: value.addedChildIds })
    },
  })

  const addedChildIds = useStore(settingsForm.store, (s) => s.values.addedChildIds)
  const canSubmitSettings = useStore(settingsForm.store, (s) => s.canSubmit)

  // ── Schedule form ──
  const scheduleForm = useForm({
    defaultValues: {
      startDate: initialStartDate,
      endDate: initialEndDate,
    },
    validators: { onSubmit: scheduleSchema },
    onSubmit: async ({ value }) => {
      await onUpdateSchedule({ startDate: value.startDate, endDate: value.endDate })
      onScheduleSuccess?.()
      setStep('settings')
    },
  })

  const canSubmitSchedule = useStore(scheduleForm.store, (s) => s.canSubmit)
  const startDate = useStore(scheduleForm.store, (s) => s.values.startDate)

  // ── Derived ──
  const allParticipants = [...participants, ...addedChildren]
  const displayedParticipants = allParticipants.filter(
    (p) => !participantSearch || p.name.toLowerCase().includes(participantSearch.toLowerCase()),
  )

  // Picker excludes everyone already enrolled plus anyone queued during this session.
  const pickerChildren = availableChildren.filter(
    (c) => !enrolledChildIds.includes(c.id) && !addedChildIds.includes(c.id),
  )
  const allPickerSelected = pickerChildren.length > 0 && selectedIds.length === pickerChildren.length
  const somePickerSelected = selectedIds.length > 0 && selectedIds.length < pickerChildren.length

  const hasPickerFilters = !!availableSearch || !!availableTeamId
  const isPickerEmpty = pickerChildren.length === 0 && !isLoadingAvailable
  const isNoClassMember = isPickerEmpty && !hasPickerFilters && !!showDash && (teams?.length ?? 0) === 0

  function toggleSelected(id: string) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function toggleAllSelected() {
    if (allPickerSelected) {
      setSelectedIds([])
    } else {
      setSelectedIds(pickerChildren.map((c) => c.id))
    }
  }

  function handleAddParticipants() {
    const newChildren = availableChildren.filter((c) => selectedIds.includes(c.id))
    setAddedChildren((prev) => [...prev, ...newChildren])
    settingsForm.setFieldValue('addedChildIds', [...addedChildIds, ...selectedIds])
    setSelectedIds([])
    setStep('settings')
  }

  function handleClose(v: boolean) {
    if (!v) {
      setStep('settings')
      setSelectedIds([])
      setParticipantSearch('')
      setAddedChildren([])
      settingsForm.reset()
      scheduleForm.reset()
    }
    onOpenChange(v)
  }

  const participantCount = enrolledChildIds.length + addedChildIds.length

  return (
    <>
      {/* ── Step 1: Settings ── */}
      <Dialog open={open && step === 'settings'} onOpenChange={handleClose}>
        <DialogContent className='srd:max-w-lg srd:w-full' showCloseButton>
          <DialogHeader>
            <DialogTitle>Pengaturan Tugas</DialogTitle>
            <DialogDescription>Atur penamaan tugas, peserta, dan jadwal tugas</DialogDescription>
          </DialogHeader>

          <settingsForm.Field name='title'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field data-invalid={isInvalid || undefined}>
                  <FieldLabel>Nama tugas</FieldLabel>
                  <Input
                    size='sm'
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    aria-invalid={isInvalid}
                    disabled={readOnly}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </settingsForm.Field>

          <div>
            <div className='srd:flex srd:items-center srd:justify-between srd:px-4 srd:py-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-grey-100')}>
                {participantCount} peserta
              </p>
              <div className='srd:relative'>
                <SearchIcon className='srd:absolute srd:left-3 srd:top-1/2 srd:-translate-y-1/2 srd:size-4 srd:text-grey-60 srd:pointer-events-none' />
                <Input
                  size='sm'
                  value={participantSearch}
                  onChange={(e) => setParticipantSearch(e.target.value)}
                  placeholder='Cari peserta'
                  className='srd:pl-9 srd:w-44'
                />
              </div>
            </div>

            <Separator />

            <div className='srd:max-h-[230px] srd:overflow-y-auto'>
              {displayedParticipants.length === 0 && (
                <div className='srd:flex srd:flex-col srd:items-center srd:gap-2 srd:py-10 srd:text-center'>
                  <p className={typographyVariants({ variant: 'label-lg' })}>Tidak ada hasil</p>
                  <p className={typographyVariants({ variant: 'body-sm' })}>Coba ubah kata kunci pencarian.</p>
                </div>
              )}
              {displayedParticipants.map((child) => (
                <React.Fragment key={child.id}>
                  <ParticipantRow child={child} type={type} />
                </React.Fragment>
              ))}
            </div>
          </div>

          <DialogFooter className='srd:flex srd:gap-2 srd:flex-wrap'>
            {readOnly ? (
              <Button onClick={() => handleClose(false)}>Tutup</Button>
            ) : (
              <>
                <Button
                  variant='ghost'
                  className='srd:text-neutral-black'
                  onClick={() => {
                    setSelectedIds([])
                    setStep('add-participants')
                  }}
                >
                  <PlusCircleIcon className='srd:size-4 srd:mr-2' />
                  Tambah peserta
                </Button>
                <Button variant='outline' onClick={() => setStep('schedule')}>
                  <CalendarIcon className='srd:size-4 srd:mr-2' />
                  Jadwal tugas
                </Button>
                <Button
                  disabled={!canSubmitSettings || isSaving}
                  loading={isSaving}
                  onClick={() => void settingsForm.handleSubmit()}
                >
                  Simpan
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Step 2: Add Participants ── */}
      <Dialog
        open={open && step === 'add-participants'}
        onOpenChange={(v) => {
          if (!v) handleClose(false)
        }}
      >
        <DialogContent className='srd:max-w-lg srd:w-full' showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Pilih peserta tugas</DialogTitle>
            <DialogDescription>Pilih dan tambahkan peserta ke dalam tugas yang sedang berjalan.</DialogDescription>
          </DialogHeader>

          <div className={cn('srd:flex srd:gap-2', type === 'school' ? 'srd:flex-row' : 'srd:flex-col')}>
            {type === 'school' && (
              <SchoolClassSelectBase
                size='sm'
                value={availableTeamId ?? ''}
                onValueChange={(v) => onAvailableTeamChange?.(v || undefined)}
                teams={teams ?? []}
                isLoading={isLoadingTeams}
                showDash={showDash}
                showNoClass={!showDash}
                allowAll={!showDash}
                className='srd:flex-1'
              />
            )}
            <div className='srd:relative srd:flex-1'>
              <SearchIcon className='srd:absolute srd:left-3 srd:top-1/2 srd:-translate-y-1/2 srd:size-4 srd:text-grey-60 srd:pointer-events-none' />
              <Input
                size='sm'
                value={availableSearch}
                onChange={(e) => onAvailableSearchChange(e.target.value)}
                placeholder='Cari'
                className='srd:pl-9'
              />
            </div>
          </div>

          <div>
            <div className='srd:flex srd:items-center srd:justify-between srd:px-4 srd:py-2'>
              <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-blue-100')}>
                {selectedIds.length} terpilih
              </p>
              <Checkbox
                checked={allPickerSelected}
                indeterminate={somePickerSelected}
                onCheckedChange={toggleAllSelected}
                aria-label='Pilih semua'
                disabled={pickerChildren.length === 0}
              />
            </div>

            <Separator />

            <div className='srd:max-h-[230px] srd:overflow-y-auto'>
              {isLoadingAvailable && (
                <div className='srd:flex srd:flex-col srd:gap-1 srd:p-4'>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className='srd:h-14 srd:w-full srd:rounded-lg' />
                  ))}
                </div>
              )}

              {!isLoadingAvailable && isPickerEmpty && (
                <div className='srd:flex srd:flex-col srd:items-center srd:gap-2 srd:py-10 srd:text-center'>
                  <p className={typographyVariants({ variant: 'label-lg' })}>
                    {isNoClassMember
                      ? 'Tidak ada murid tersedia'
                      : hasPickerFilters
                        ? 'Tidak ada hasil'
                        : 'Semua peserta sudah ditambahkan'}
                  </p>
                  <p className={cn(typographyVariants({ variant: 'body-sm' }))}>
                    {isNoClassMember
                      ? 'Kamu belum ditugaskan mengampu di kelas mana pun di sekolah ini.'
                      : hasPickerFilters
                        ? 'Coba ubah filter atau kata kunci pencarian.'
                        : 'Tidak ada peserta lain yang bisa ditambahkan.'}
                  </p>
                </div>
              )}

              {!isLoadingAvailable &&
                pickerChildren.map((child) => (
                  <PickerRow
                    key={child.id}
                    child={child}
                    selected={selectedIds.includes(child.id)}
                    onToggle={toggleSelected}
                    type={type}
                  />
                ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setSelectedIds([])
                setStep('settings')
              }}
            >
              Kembali
            </Button>
            <Button disabled={selectedIds.length === 0} onClick={handleAddParticipants}>
              Tambahkan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Step 3: Schedule ── */}
      <Dialog
        open={open && step === 'schedule'}
        onOpenChange={(v) => {
          if (!v) handleClose(false)
        }}
      >
        <DialogContent className='srd:max-w-lg srd:w-full' showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Atur jadwal tugas</DialogTitle>
            <DialogDescription>
              Atur ulang jendela waktu {childLabel} dapat menyelesaikan semua aktivitas dalam tugas ini.
            </DialogDescription>
          </DialogHeader>

          <div className='srd:flex srd:flex-col srd:gap-4 srd:mt-2'>
            <scheduleForm.Field name='startDate'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                return (
                  <Field data-invalid={isInvalid || undefined}>
                    <FieldLabel>Jadwal mulai</FieldLabel>
                    <DatePresetPicker
                      presets={START_PRESETS}
                      value={field.state.value}
                      onChange={(v) => {
                        field.handleChange(v ?? new Date())
                        scheduleForm.setFieldValue('endDate', null)
                      }}
                      trigger={StartTrigger}
                      disabled={hasStarted}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </scheduleForm.Field>

            <scheduleForm.Field name='endDate'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                return (
                  <Field data-invalid={isInvalid || undefined}>
                    <FieldLabel>Jatuh tempo</FieldLabel>
                    <DatePresetPicker
                      key={startDate.getTime()}
                      presets={DUE_PRESETS}
                      value={field.state.value}
                      onChange={(v) => field.handleChange(v)}
                      trigger={DueTrigger}
                      minDate={startDate}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </scheduleForm.Field>
          </div>

          <DialogFooter>
            <Button variant='outline' disabled={isUpdatingSchedule} onClick={() => setStep('settings')}>
              Kembali
            </Button>
            <Button
              disabled={!canSubmitSchedule || isUpdatingSchedule}
              loading={isUpdatingSchedule}
              onClick={() => void scheduleForm.handleSubmit()}
            >
              Perbarui
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function EditCourseDialog({
  type,
  open,
  onOpenChange,
  courseId,
  initialTitle,
  initialStartDate,
  initialEndDate,
  initialCreatedAt,
  initialParticipantIds,
  readOnly = false,
  onSuccess,
  onScheduleSuccess,
}: EditCourseDialogProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()
  const authClient = useAuthClient()
  const { data: memberRole, isPending: isRolePending } = authClient.useActiveMemberRole()

  const isAdminOrOwner = memberRole?.role === 'owner' || memberRole?.role === 'admin'

  const [availableSearch, setAvailableSearch] = useState('')
  const [availableTeamId, setAvailableTeamId] = useState<string | undefined>(undefined)

  const myTeamsOnly = type === 'school' && !isAdminOrOwner && !availableTeamId && !isRolePending

  const { data: allChildrenData } = useQuery(
    trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      includeSchoolClass: true,
    }),
  )

  const { data: availableChildrenData, isLoading: isLoadingAvailable } = useQuery(
    trpc.myOrganization.listChildren.queryOptions({
      pageSize: 'all',
      search: availableSearch || undefined,
      teamId: availableTeamId,
      myTeamsOnly: myTeamsOnly || undefined,
      includeSchoolClass: true,
    }),
  )

  const allTeamsQuery = useQuery({
    ...trpc.myOrganization.getAllTeams.queryOptions(),
    enabled: type === 'school' && !isRolePending && isAdminOrOwner,
  })

  const myTeamsQuery = useQuery({
    ...trpc.myOrganization.getMyTeams.queryOptions(),
    enabled: type === 'school' && !isRolePending && !isAdminOrOwner,
  })

  const isLoadingTeams =
    type === 'school' && (isRolePending || (isAdminOrOwner ? allTeamsQuery.isLoading : myTeamsQuery.isLoading))

  const allChildren = allChildrenData?.data ?? []

  const participants = useMemo(
    () =>
      allChildren
        .filter((c) => initialParticipantIds.includes(c.id))
        .map((c) => ({
          id: c.id,
          name: c.name ?? '',
          image: c.image,
          premiumId: c.premiumId,
          schoolClass: c.schoolClass,
        })),
    [allChildrenData?.data, initialParticipantIds],
  )

  const availableChildren = useMemo(
    () =>
      (availableChildrenData?.data ?? [])
        .filter((c) => !initialParticipantIds.includes(c.id))
        .map((c) => ({
          id: c.id,
          name: c.name ?? '',
          image: c.image,
          premiumId: c.premiumId,
          schoolClass: c.schoolClass,
        })),
    [availableChildrenData?.data, initialParticipantIds],
  )

  const rawTeamsData = isAdminOrOwner ? allTeamsQuery.data : myTeamsQuery.data
  const teams = useMemo(
    () =>
      rawTeamsData?.map((t) => ({
        id: t.id,
        name: t.name,
        image: (t as { image?: string | null }).image ?? null,
      })) ?? [],
    [rawTeamsData],
  )

  const course = useMemo(
    () => ({
      id: courseId,
      title: initialTitle,
      startDate: initialStartDate,
      endDate: initialEndDate,
      createdAt: initialCreatedAt,
    }),
    [courseId, initialTitle, initialStartDate, initialEndDate, initialCreatedAt],
  )

  const hasStarted = useMemo(() => initialStartDate == null || initialStartDate <= new Date(), [initialStartDate])

  const saveMutation = useMutation(
    trpc.lesson.editCourse.mutationOptions({
      onSuccess: async () => {
        toast.success('Tugas berhasil diperbarui')
        onOpenChange(false)
        onSuccess?.()
        await queryClient.invalidateQueries({
          queryKey: trpc.lesson.getScoreboardData.infiniteQueryKey(),
        })
      },
      onError: () => {
        toast.error('Gagal memperbarui tugas, coba lagi')
      },
    }),
  )

  const scheduleMutation = useMutation(
    trpc.lesson.editCourse.mutationOptions({
      onSuccess: () => {
        toast.success('Jadwal berhasil diperbarui')
      },
      onError: () => {
        toast.error('Gagal memperbarui jadwal, coba lagi')
      },
    }),
  )

  async function handleSave(data: EditCourseSaveData) {
    await saveMutation.mutateAsync({
      courseId,
      data: { title: data.title },
      // Additions only. Saving this dialog renames the task and enrols anyone newly picked; it
      // can never unenrol, so a participant list that has not finished loading is harmless.
      addChildIds: data.addChildIds.length > 0 ? data.addChildIds : undefined,
    })
  }

  async function handleUpdateSchedule(data: EditCourseScheduleData) {
    await scheduleMutation.mutateAsync({
      courseId,
      data: {
        startDate: data.startDate,
        endDate: data.endDate ?? undefined,
      },
    })
  }

  return (
    <EditCourseDialogBase
      type={type}
      open={open}
      onOpenChange={onOpenChange}
      key={initialParticipantIds.join('|')}
      course={course}
      participants={participants}
      initialParticipantIds={initialParticipantIds}
      availableChildren={availableChildren}
      isLoadingAvailable={isLoadingAvailable}
      teams={teams}
      isLoadingTeams={isLoadingTeams}
      showDash={!isAdminOrOwner}
      availableSearch={availableSearch}
      onAvailableSearchChange={setAvailableSearch}
      availableTeamId={availableTeamId}
      onAvailableTeamChange={setAvailableTeamId}
      hasStarted={hasStarted}
      readOnly={readOnly}
      onSave={handleSave}
      isSaving={saveMutation.isPending}
      onUpdateSchedule={handleUpdateSchedule}
      isUpdatingSchedule={scheduleMutation.isPending}
      onScheduleSuccess={onScheduleSuccess}
    />
  )
}
